import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import useTranslations from "@/shared/lang/useTranslations";
import { OrganizationStatus } from "@/shared/types/organization.entity";

import {
  ORGANIZATION_SEARCH_MIN_LENGTH,
  ORGANIZATION_STATUS_LABEL_KEYS,
  PAYOUT_BATCHES_FILTER_LABEL_KEYS,
  PAYOUT_BATCHES_FILTERS,
} from "../constants/organizationsAdmin.constants";
import type { PayoutBatchesFilter } from "../types/organizationsAdmin.types";

const ALL_STATUSES = "__all__";

export interface OrganizationsAdminFiltersProps {
  search: string;
  onSearchChange: (search: string) => void;
  searchTooShort: boolean;
  status: OrganizationStatus | null;
  onStatusChange: (status: OrganizationStatus | null) => void;
  payoutBatches: PayoutBatchesFilter;
  onPayoutBatchesChange: (filter: PayoutBatchesFilter) => void;
}

const labelClass = "font-mono text-[10px] tracking-wider text-muted-foreground uppercase";

export function OrganizationsAdminFilters({
  search,
  onSearchChange,
  searchTooShort,
  status,
  onStatusChange,
  payoutBatches,
  onPayoutBatchesChange,
}: OrganizationsAdminFiltersProps) {
  const { t } = useTranslations("organizations");

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
      <div className="flex flex-col gap-1.5">
        <label htmlFor="organizations-search" className={labelClass}>
          {t("filter_search_label")}
        </label>
        <ColocaSearchInput
          id="organizations-search"
          value={search}
          onValueChange={onSearchChange}
          placeholder={t("filter_search_placeholder")}
          aria-describedby={searchTooShort ? "organizations-search-hint" : undefined}
        />
        {searchTooShort && (
          <p id="organizations-search-hint" className="text-xs text-muted-foreground">
            {t("filter_search_min_length", { count: ORGANIZATION_SEARCH_MIN_LENGTH })}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="organizations-status" className={labelClass}>
          {t("filter_status_label")}
        </label>
        <ColocaSelect
          id="organizations-status"
          value={status ?? ALL_STATUSES}
          onValueChange={(value) =>
            onStatusChange(value === ALL_STATUSES ? null : (value as OrganizationStatus))
          }
          options={[
            { value: ALL_STATUSES, label: t("filter_status_all") },
            ...Object.values(OrganizationStatus).map((value) => ({
              value,
              label: t(ORGANIZATION_STATUS_LABEL_KEYS[value]),
            })),
          ]}
          aria-label={t("filter_status_label")}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="organizations-payout-batches" className={labelClass}>
          {t("filter_payout_batches_label")}
        </label>
        <ColocaSelect
          id="organizations-payout-batches"
          value={payoutBatches}
          onValueChange={(value) => onPayoutBatchesChange(value as PayoutBatchesFilter)}
          options={PAYOUT_BATCHES_FILTERS.map((value) => ({
            value,
            label: t(PAYOUT_BATCHES_FILTER_LABEL_KEYS[value]),
          }))}
          aria-label={t("filter_payout_batches_label")}
        />
      </div>
    </div>
  );
}
